import * as React from 'react';
import { useDispatch } from 'react-redux';

import { useSelector } from '@app/hooks/useSelector';
import { useQueryParams } from '@app/hooks/useQueryParams';
import { Nullable } from '@app/objects/Utility';
import { formatPhone } from '@app/services/phone';
import { getActionCreators } from '@app/store/MainPage/ListActions';
import { DispatchType, RootStore } from '@app/store';

interface OwnProps {
	minimized?: boolean;
}

type DropdownKey = 'destinations' | 'about';

const WEBFLOW_CSS_ID = 'ovc-webflow-header-css';
const WEBFLOW_JS_ID = 'ovc-webflow-header-js';
const GLOBAL_CSS_ID = 'ovc-new-global-style';
const GLOBAL_CSS_HREF = '/css/new_global_style.css';
const ASSETS = {
	webflowCssHref:
		'https://cdn.prod.website-files.com/68503ae3ed503a1573553872/css/onlinevacationcenter.shared.1b0840611.css',
	webflowScriptSrc:
		'https://cdn.prod.website-files.com/68503ae3ed503a1573553872/js/onlinevacationcenter.450f21b6.593bd180f0322b42.js',
	headerLogoSrc:
		'https://cdn.prod.website-files.com/68503ae3ed503a1573553872/693bc9f5a16ec37e0da259c7_Layer_1.png',
	phoneIconPath:
		'M7.5 2.70834C7.75557 2.70834 7.98538 2.86393 8.0803 3.10122L9.74697 7.26788C9.86089 7.55271 '
		+ '9.75127 7.87811 9.48823 8.03594L7.91508 8.97983C8.66174 10.2677 9.73232 11.3383 11.0202 12.0849'
		+ 'L11.9641 10.5118C12.1219 10.2487 12.4473 10.1391 12.7321 10.253L16.8988 11.9197C17.1361 '
		+ '12.0146 17.2917 12.2444 17.2917 12.5V15.8333C17.2917 16.4411 17.0502 17.024 16.6205 17.4538C16.1907 '
		+ '17.8836 15.6078 18.125 15 18.125C14.9874 18.125 14.9747 18.1246 14.9621 18.1239C11.5591 17.9171 '
		+ '8.34946 16.472 5.93875 14.0613C3.52804 11.6505 2.08295 8.4409 1.87615 5.03791C1.87538 5.02529 1.875 '
		+ '5.01265 1.875 5C1.875 4.39222 2.11644 3.80932 2.54621 3.37955C2.97598 2.94978 3.55888 2.70834 4.16667 '
		+ '2.70834L7.5 2.70834Z',
};
const HEADER_LOGO_SRC_SET = [
	'https://cdn.prod.website-files.com/68503ae3ed503a1573553872/693bc9f5a16ec37e0da259c7_Layer_1-p-500.png 500w',
	'https://cdn.prod.website-files.com/68503ae3ed503a1573553872/693bc9f5a16ec37e0da259c7_Layer_1.png 978w',
].join(', ');
const DEFAULT_PHONE = '8007809002';
const RF_KEY = 'rf';
const CID_KEY = 'cid';

const ensureStyles = () => {
	if (!document.getElementById(WEBFLOW_CSS_ID)) {
		const css = document.createElement('link');
		css.id = WEBFLOW_CSS_ID;
		css.rel = 'stylesheet';
		css.type = 'text/css';
		css.href = ASSETS.webflowCssHref;
		css.crossOrigin = 'anonymous';
		document.head.appendChild(css);
	}
};

const ensureGlobalStyles = () => {
	if (!document.getElementById(GLOBAL_CSS_ID)) {
		const css = document.createElement('link');
		css.id = GLOBAL_CSS_ID;
		css.rel = 'stylesheet';
		css.type = 'text/css';
		css.href = GLOBAL_CSS_HREF;
		document.head.appendChild(css);
	}
};

const ensureScript = () => {
	if (!document.getElementById(WEBFLOW_JS_ID)) {
		const script = document.createElement('script');
		script.id = WEBFLOW_JS_ID;
		script.src = ASSETS.webflowScriptSrc;
		script.async = true;
		document.body.appendChild(script);
	}
};

export const Header: React.FC<OwnProps> = () => {
	const rootRef = React.useRef<HTMLDivElement | null>(null);
	const [openDropdown, setOpenDropdown] = React.useState<DropdownKey | null>(null);
	const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
	const phone = useSelector((state: RootStore) => state.header.item?.phone);
	const dispatch = useDispatch<DispatchType<'header'>>();

	const handleQuery = React.useCallback((values: Record<string, Nullable<string>>) => {
		const rf = values[RF_KEY];
		const cid = values[CID_KEY];

		const params: Array<string> = [];
		if (rf) params.push(`rf=${rf}`);
		if (cid) params.push(`cid=${cid}`);

		const query = params.join('&');
		const factory = getActionCreators(`api/pages/v1/header?${query}`, 'header');
		dispatch(factory.request());
	}, [dispatch]);

	useQueryParams([RF_KEY, CID_KEY], handleQuery);

	const phoneNumber = phone || DEFAULT_PHONE;
	const phoneDisplay = formatPhone(phoneNumber);
	const phoneHref = `tel:${phoneNumber}`;

	const toggleDropdown = React.useCallback((key: DropdownKey) => {
		setOpenDropdown((previous) => (previous === key ? null : key));
	}, []);

	const closeMenus = React.useCallback(() => {
		setOpenDropdown(null);
		setIsMobileMenuOpen(false);
	}, []);

	React.useEffect(() => {
		document.documentElement.classList.add('w-mod-js');
		if ('ontouchstart' in window) {
			document.documentElement.classList.add('w-mod-touch');
		}

		ensureStyles();
		ensureGlobalStyles();
		ensureScript();
	}, []);

	React.useEffect(() => {
		const handleOutsideMouseDown = (event: MouseEvent) => {
			if (!rootRef.current?.contains(event.target as Node)) {
				closeMenus();
			}
		};

		const handleEscape = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				closeMenus();
			}
		};

		document.addEventListener('mousedown', handleOutsideMouseDown);
		document.addEventListener('keydown', handleEscape);

		return () => {
			document.removeEventListener('mousedown', handleOutsideMouseDown);
			document.removeEventListener('keydown', handleEscape);
		};
	}, [closeMenus]);

	React.useEffect(() => {
		const handleResize = () => {
			if (window.innerWidth > 991) {
				closeMenus();
			}
		};

		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, [closeMenus]);

	return (
		<div id="header" className="page_wrapper ovc-header-scope" ref={rootRef}>
			<div data-wf--navigation---all--variant="base" className="nav_all_component">
				<div
					data-wf--navigation---main--variant="solid"
					data-animation="default"
					data-collapse="medium"
					data-duration="400"
					data-easing="ease"
					data-easing2="ease"
					role="banner"
					className="nav_component max-width-full padding-vertical padding-custom2 w-nav"
				>
					<div className="nav_container padding-horizontal padding-medium">
						<a href="https://www.onlinevacationcenter.com/" className="w-inline-block">
							<div data-wf--logotype--variant="dark" className="logotype-container">
								<img
									src={ASSETS.headerLogoSrc}
									loading="lazy"
									width={79}
									sizes="(max-width: 479px) 100vw, 79px"
									alt="Online Vacation Center"
									srcSet={HEADER_LOGO_SRC_SET}
									className="image-24 w-variant-99c3a15f-1d1d-33fa-ac9b-25394975ef81 dark-logo"
								/>
							</div>
						</a>

						<nav
							role="navigation"
							className={`nav_menu w-nav-menu ${isMobileMenuOpen ? 'w--open is-open' : ''}`}
						>
							<div className="text-weight-semibold">
								<div className="menu_items-container">
									<a
										href="https://www.onlinevacationcenter.com/search-exclusive-packages"
										className="nav_menu_link w-nav-link"
										onClick={closeMenus}
									>
										Exclusive Packages
									</a>
									<div
										data-hover="false"
										data-delay={0}
										className={`dropdown w-dropdown ${openDropdown === 'destinations' ? 'w--open' : ''}`}
									>
										<button
											type="button"
											className={`dropdown-toggle w-dropdown-toggle ${openDropdown === 'destinations' ? 'w--open' : ''}`}
											onClick={() => toggleDropdown('destinations')}
											aria-expanded={openDropdown === 'destinations'}
											aria-haspopup="menu"
										>
											<div className="text-block-9">Explore Destinations</div>
											<div className="icon w-icon-dropdown-toggle" />
										</button>
										<nav
											className={`dropdown-list w-dropdown-list ${openDropdown === 'destinations' ? 'w--open' : ''}`}
										>
											<a
												href="https://www.onlinevacationcenter.com/destinations"
												className="dropdown-link-2 w-dropdown-link"
												onClick={closeMenus}
											>
												All Destinations
											</a>
											<a
												href="https://res.onlinevacationcenter.com/swift/cruise"
												target="_blank"
												rel="noreferrer"
												className="dropdown-link-3 w-dropdown-link"
												onClick={closeMenus}
											>
												Cruise Lines
											</a>
										</nav>
									</div>
									<div
										data-hover="false"
										data-delay={0}
										className={`dropdown w-dropdown ${openDropdown === 'about' ? 'w--open' : ''}`}
									>
										<button
											type="button"
											className={`dropdown-toggle w-dropdown-toggle ${openDropdown === 'about' ? 'w--open' : ''}`}
											onClick={() => toggleDropdown('about')}
											aria-expanded={openDropdown === 'about'}
											aria-haspopup="menu"
										>
											<div className="text-block-9">About Us</div>
											<div className="icon w-icon-dropdown-toggle" />
										</button>
										<nav
											className={`dropdown-list-2 w-dropdown-list ${openDropdown === 'about' ? 'w--open' : ''}`}
										>
											<a
												href="https://www.onlinevacationcenter.com/why-ovc"
												className="dropdown-link-5 w-dropdown-link"
												onClick={closeMenus}
											>
												Our Distinction
											</a>
											<a
												href="https://www.onlinevacationcenter.com/managers"
												className="dropdown-link-6 w-dropdown-link"
												onClick={closeMenus}
											>
												Vacation Managers
											</a>
											<a
												href="https://www.onlinevacationcenter.com/reviews"
												className="dropdown-link-7 w-dropdown-link"
												onClick={closeMenus}
											>
												Reviews
											</a>
											<a
												href="https://www.onlinevacationcenter.com/contact-us"
												className="dropdown-link-7 w-dropdown-link"
												onClick={closeMenus}
											>
												Contact us
											</a>
										</nav>
									</div>
									<a
										href="https://apps.onlinevacationcenter.com/cp/login.cfm"
										target="_blank"
										rel="noreferrer"
										className="nav_menu_link w-nav-link"
										onClick={closeMenus}
									>
										Manage Trips
									</a>
									<a
										href="https://www.onlinevacationcenter.com/request-a-quote"
										className="nav_menu_link w-nav-link"
										onClick={closeMenus}
									>
										Request a Quote
									</a>
								</div>
							</div>

							<div id="phone-ref-cid" className="button w-inline-block">
								<div className="button-label">
									<a href={phoneHref}><span>{phoneDisplay}</span></a>
								</div>
							</div>
						</nav>

						<div className="div-block-97">
							<a href={phoneHref} className="link-block-3 w-inline-block">
								<span className="header-phone-inline">
									<span>{phoneDisplay}</span>
								</span>
								<svg
									className="icon-4"
									width="20"
									height="20"
									viewBox="0 0 20 20"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
									aria-hidden="true"
								>
									<path
										fillRule="evenodd"
										clipRule="evenodd"
										d={ASSETS.phoneIconPath}
										fill="currentColor"
									/>
								</svg>
							</a>
							<button
								type="button"
								className={`nav_button w-nav-button ${isMobileMenuOpen ? 'w--open is-open' : ''}`}
								onClick={() => setIsMobileMenuOpen((current) => !current)}
								aria-expanded={isMobileMenuOpen}
								aria-label="Toggle navigation menu"
							>
								<div className="icon-3 w-icon-nav-menu" />
							</button>
						</div>
					</div>
				</div>

				<div className="div-block-98">
					<div
						data-wf--navigation---2nd-level2--variant="base"
						className="nav_lvl2_component padding-vertical padding-custom-nav2ndlvl"
					>
						<div className="padding-horizontal padding-medium">
							<div className="nav_lvl2_container">
								<a href="https://www.onlinevacationcenter.com/managers" className="nav_lvl2_link">
									Your Personal Vacation Managers® since 1972
								</a>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
